import React, { useState, useRef } from "react";
import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  Toolbar,
  Divider,
  IconButton,
  Drawer,
} from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import CssBaseline from "@mui/material/CssBaseline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MenuIcon from "@mui/icons-material/Menu";
import DrawerMenuItems from "./DrawerMenuItems";
import NavbarPatientSearcher from "../navbar-patient-searcher";
import { DRAWER_WIDTH } from "../../utils/constants";
import { getAppBarHeading } from "../../utils";
import { useAuthUser } from "../../store/AuthUserContextProvider";
import PropTypes from "prop-types";

const openedMixin = (theme) => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  transition: "0.6s",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
});

const AppBar = styled(MuiAppBar, {
  // shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  [theme.breakpoints.up("md")]: {
    // zIndex: theme.zIndex.drawer + 1,
  },
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),

  ...(open && {
    [theme.breakpoints.up("md")]: {
      width: `calc(100% - ${DRAWER_WIDTH}px)`,
      marginLeft: { DRAWER_WIDTH },
    },
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  position: "relative",
  ...theme.mixins.toolbar,
  cursor: "default",
}));

function PageLayout({ children }) {
  const theme = useTheme();

  const { authUser } = useAuthUser();

  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        open={mobileOpen}
        sx={{ background: "rgba(0,0,0,.65)", backdropFilter: "blur(50px)" }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{
              marginRight: "36px",
              ...(mobileOpen && {
                display: { sm: "block", md: "none" },
              }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: { xs: "none", sm: "flex" },justifyContent:'space-between',width:'100%' }}>
            <Typography
              style={{
                height: "100%",
                width: "100%",
                fontSize: 15,
                fontWeight: 500,
              }}
              noWrap
              variant="h4"
              component="b"
            >
              <span>{getAppBarHeading(window.location.pathname)}</span>
              <br />
              <span>{authUser?.Role}</span>
            </Typography>
            <Typography
              style={{
                height: "100%",
                width: "100%",
                fontSize: 15,
                fontWeight: 500,
              }}
              noWrap
              variant="h4"
              component="b"
            >
              <span>Accession:XXXXXXX</span>
              <br />
              <span>User:{authUser?.UserName}</span>
            </Typography>
            <Typography
              style={{
                height: "100%",
                width: "100%",
                fontSize: 15,
                fontWeight: 500,
              }}
              variant="h4"
              component="b"
            >
              <span>
                Date:{new Date().toLocaleDateString("en-US")}{" "}
                {new Date().getHours()}:{new Date().getMinutes()}
              </span>
              <br />
              <span>Token expire:{new Date().toLocaleDateString("en-US")}</span>
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              height: "100%",
            }}
          >
            <NavbarPatientSearcher />
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
        aria-label="mailbox folders"
      >
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
            },
          }}
        >
          <DrawerMenuItems />
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            width: DRAWER_WIDTH,
            height: "100%",
            flexShrink: 0,
            whiteSpace: "nowrap",
            flexDirection: "column",
            position: "fixed",
            transform: "translate(0,0)",
            boxSizing: "border-box",
            paddingBottom: "38px",
            zIndex: "100",

            ...(mobileOpen && {
              ...openedMixin(theme),
              "& .MuiDrawer-paper": openedMixin(theme),
            }),
            ...(!mobileOpen && {
              ...closedMixin(theme),
              "& .MuiDrawer-paper": closedMixin(theme),
            }),
          }}
          open={mobileOpen}
        >
          <DrawerHeader>
            <Box
              style={{
                height: "100%",
                width: "100%",
                position: "absolute",
                left: 0,
                top: 0,
                fontSize: 30,
                fontWeight: 900,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                paddingLeft: 10,
                background: "rgba(0,0,0,.65)",
              }}
            >
              OneRad
            </Box>
            <IconButton onClick={handleDrawerClose}>
              {theme.direction === "rtl" ? (
                <ChevronRightIcon />
              ) : (
                <ChevronLeftIcon />
              )}
            </IconButton>
          </DrawerHeader>
          <Divider />
          <DrawerMenuItems />
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: "100%",
          position: "relative",
          p : 3,
          paddingBottom: "38px",
          overflow: "auto",
          width: { xs: `calc(100% - ${DRAWER_WIDTH}px)`, sm: "100%" },
          ...(!mobileOpen && {
            marginLeft: { xs:0,sm: `-${DRAWER_WIDTH}px` },
          }),
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          transition: "0.3s",
        }}
      >
        <DrawerHeader props={children} />

        {children}
      </Box>
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          width: "100%",
          padding: "6px",
          fontSize: "12px",
          background: "rgba(0,0,0,.65)",
          backdropFilter: "blur(50px)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: "rgba(225,225,225,.7)",
        }}
      >
        support :{" "}
        <a
          style={{ marginLeft: 4, fontWeight: 500 }}
          href="https://helpdesk.onegig.co.za/"
          rel="noreferrer"
          target="_blank"
        >
          https://helpdesk.onegig.co.za/
        </a>
      </Box>
    </Box>
  );
}

export default PageLayout;
